// Range Model - Valtio-based reactive state management for min-max fields
import debounce from "debounce";
import {proxy} from "valtio";

import {IQueryParamProvider} from "@/shared/contracts";

import {BaseField, BaseFieldConfig} from "./base-field";
import {ValidationError} from "./types";

interface RangeConfig extends BaseFieldConfig {
    minValue?: number | null;
    maxValue?: number | null;
    minLabel?: string;
    maxLabel?: string;
    debounceDelay?: number;
}

class RangeClass extends BaseField implements IQueryParamProvider {
    // Reactive state properties (specific to range)
    _minValue: number | null;
    _maxValue: number | null;

    // Non-reactive properties
    private readonly initialMinValue: number | null;
    private readonly initialMaxValue: number | null;
    private readonly minLabel: string;
    private readonly maxLabel: string;
    private readonly debouncedEmitChange: (value: string) => void;
    private readonly debouncedEmitInput: (value: string) => void;

    constructor(config: RangeConfig = {}) {
        super(config);

        this.initialMinValue = config.minValue ?? null;
        this.initialMaxValue = config.maxValue ?? null;
        this._minValue = this.initialMinValue;
        this._maxValue = this.initialMaxValue;

        this.minLabel = config.minLabel || "Min";
        this.maxLabel = config.maxLabel || "Max";

        this.debouncedEmitInput = debounce((value: string) => {
            this.events.emit("input", value);
        }, 10);

        const debounceDelay = config.debounceDelay ?? 100;
        this.debouncedEmitChange = debounce((value: string) => {
            this.events.emit("change", value);
        }, debounceDelay);
    }

    // Computed getters
    get minValue(): number | null {
        return this._minValue;
    }

    get maxValue(): number | null {
        return this._maxValue;
    }

    get hasValue(): boolean {
        return this._minValue !== null || this._maxValue !== null;
    }

    // Set values (with "change" event - slow debounce)
    setMinValue(value: number | null): void {
        if (this._disabled) return;

        this._minValue = value;
        this._errors = [];
        this.validate();
        this.debouncedEmitChange(String(value ?? ""));
    }

    setMaxValue(value: number | null): void {
        if (this._disabled) return;

        this._maxValue = value;
        this._errors = [];
        this.validate();
        this.debouncedEmitChange(String(value ?? ""));
    }

    // Set values with "input" event (fast debounce for slider dragging)
    setMinValueInput(value: number | null): void {
        if (this._disabled) return;

        this._minValue = value;
        this._errors = [];
        this.validate();
        this.debouncedEmitInput(String(value ?? ""));
    }

    setMaxValueInput(value: number | null): void {
        if (this._disabled) return;

        this._maxValue = value;
        this._errors = [];
        this.validate();
        this.debouncedEmitInput(String(value ?? ""));
    }

    // Validate
    validate(): {isValid: boolean; error?: string} {
        // Check required
        if (this.isRequired && this._minValue === null && this._maxValue === null) {
            const error: ValidationError = {
                type: "error",
                message: this.requireMessage,
            };
            this._errors = [error];
            return {isValid: false, error: this.requireMessage};
        }

        // Check if min <= max
        if (this._minValue !== null && this._maxValue !== null && this._minValue > this._maxValue) {
            const error: ValidationError = {
                type: "error",
                message: `${this.minLabel} cannot be greater than ${this.maxLabel}`,
            };
            this._errors = [error];
            return {isValid: false, error: error.message};
        }

        // Run custom validators using base method
        const validationResult = this.runValidators(String(this._minValue ?? ""));
        if (!validationResult.isValid) {
            return validationResult;
        }

        this._errors = [];
        return {isValid: true};
    }

    // Reset to initial values
    reset(): void {
        this._minValue = this.initialMinValue;
        this._maxValue = this.initialMaxValue;
        this._errors = [];
        this.debouncedEmitChange(String(this._minValue ?? ""));
    }

    // Clear values
    clear(): void {
        this._minValue = null;
        this._maxValue = null;
        this._errors = [];
    }

    // Query params (IQueryParamProvider implementation)
    getQueryParam(paramName?: string, options?: {minPrefix?: string; maxPrefix?: string}): string {
        const params: Array<string> = [];
        const baseName = paramName || "value";

        // Support custom prefixes like "min" and "max" for Vacasa-style URLs
        const minParamName = options?.minPrefix ? `${options.minPrefix}${baseName}` : `${baseName}Min`;
        const maxParamName = options?.maxPrefix ? `${options.maxPrefix}${baseName}` : `${baseName}Max`;

        if (this._minValue !== null) {
            params.push(`${minParamName}=${this._minValue}`);
        }
        if (this._maxValue !== null) {
            params.push(`${maxParamName}=${this._maxValue}`);
        }

        return params.join("&");
    }

    // Set from query params
    setFromQueryParams(params: URLSearchParams, paramName?: string, options?: {minPrefix?: string; maxPrefix?: string}): void {
        const baseName = paramName || "value";

        // Support custom prefixes like "min" and "max" for Vacasa-style URLs
        const minParamName = options?.minPrefix ? `${options.minPrefix}${baseName}` : `${baseName}Min`;
        const maxParamName = options?.maxPrefix ? `${options.maxPrefix}${baseName}` : `${baseName}Max`;

        const minParam = params.get(minParamName);
        if (minParam !== null) {
            const minValue = Number(minParam);
            if (!isNaN(minValue)) {
                this._minValue = minValue;
            }
        }

        const maxParam = params.get(maxParamName);
        if (maxParam !== null) {
            const maxValue = Number(maxParam);
            if (!isNaN(maxValue)) {
                this._maxValue = maxValue;
            }
        }
    }
}

// Export type
export type RangeInstance = RangeClass;

// Factory function
export function Range(config: RangeConfig = {}): RangeInstance {
    return proxy<RangeInstance>(new RangeClass(config));
}
